import React from 'react';

/**
 * ErrorBoundary global — capture toute erreur React (rendu, lifecycle)
 * et affiche un message au lieu d'un écran blanc complet.
 *
 * Particulièrement utile quand :
 * - Un composant lucide-react est utilisé sans import
 * - Une API renvoie un objet/array là où on attend une string
 * - Un crash silencieux dans un modal/dialog
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log dans la console pour pouvoir diagnostiquer
    console.error('[ErrorBoundary]', error, info);
    this.setState({ info });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, info: null });
  };

  handleReload = () => {
    try { window.location.reload(); } catch (_) { /* ignore */ }
  };

  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message || String(this.state.error || 'Erreur inconnue');
      return (
        <div
          data-testid="error-boundary-fallback"
          className="min-h-[50vh] flex items-center justify-center p-6"
        >
          <div className="max-w-lg w-full bg-white border border-gold-200 rounded-2xl shadow-lg p-6">
            <div className="flex items-start gap-3">
              <div className="text-3xl">⚠️</div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-gold-700 mb-1">Une erreur est survenue</h2>
                <p className="text-sm text-slate-600 mb-3">
                  L&apos;application a rencontré un problème inattendu mais reste utilisable.
                  Vous pouvez fermer ce message ou recharger la page.
                </p>
                <details className="text-xs text-slate-500 mb-4">
                  <summary className="cursor-pointer hover:text-slate-700">Détails techniques</summary>
                  <pre className="mt-2 p-2 bg-slate-50 rounded text-[11px] overflow-auto max-h-48 whitespace-pre-wrap break-words">{msg}</pre>
                </details>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={this.handleReset}
                    data-testid="error-boundary-dismiss"
                    className="px-3 py-1.5 text-sm rounded-md bg-slate-700 hover:bg-slate-800 text-white font-semibold"
                  >
                    Fermer
                  </button>
                  <button
                    type="button"
                    onClick={this.handleReload}
                    data-testid="error-boundary-reload"
                    className="px-3 py-1.5 text-sm rounded-md bg-gold-600 hover:bg-gold-700 text-white font-semibold"
                  >
                    Recharger la page
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
